import React from 'react';
import { View, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import ProfileViewCard from '@/components/ProfileViews/ProfileViewCard';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';

/**
 * Fetches and renders a saved-View profile card for the selected person via
 * get_profile_view_card (viewer-scoped — the server omits sections the viewer
 * can't see). ProfileViewCard stays presentational; this wrapper owns the
 * query plus loading / error / unavailable states.
 *
 * Distinct states:
 * - loading: skeleton-ish spinner row
 * - error: retry + message
 * - null data: view deleted / person unavailable / not permitted
 */
const FormProfileView = ({ viewID, personID, heading = 'Current profile details', testID }) => {
    const { data, isLoading, isError, refetch, isRefetching } = Query.profileViews.card({
        viewID,
        personID,
        options: { enabled: !!viewID && !!personID },
    });

    return (
        <View style={styles.container} testID={testID}>
            {!!heading && <Text style={styles.heading}>{heading}</Text>}
            {!viewID || !personID ? (
                <Text style={styles.muted}>No profile selected.</Text>
            ) : isLoading ? (
                <View style={styles.loadingRow}>
                    <ActivityIndicator size="small" />
                    <Text style={styles.muted}>Loading profile…</Text>
                </View>
            ) : isError || !data ? (
                /* getProfileViewCard returns null both when the request fails
                   and when the view/person is gone or not permitted — surface
                   one honest "unavailable" state with retry for both. */
                <View style={styles.center} testID={testID ? `${testID}-unavailable` : undefined}>
                    <Unicon name="person-circle-outline" size={22} colorVariant="textTertiary" />
                    <Text style={styles.muted}>Profile details are unavailable — the view or profile may have been removed, or you don&apos;t have permission to see it.</Text>
                    <SolidButton
                        text="Retry"
                        onPress={() => refetch()}
                        processing={isRefetching}
                        variant="muted"
                        type="textSecondary"
                        size="sm"
                        testID={testID ? `${testID}-retry` : undefined}
                    />
                </View>
            ) : (
                <ProfileViewCard data={data} />
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, roundness }) => ({
    container: {
        marginTop: spacing.sm,
        backgroundColor: colors.surface,
        borderRadius: roundness.lg,
        padding: spacing.md,
        gap: spacing.xs,
    },
    heading: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    loadingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        paddingVertical: spacing.sm,
    },
    center: {
        alignItems: 'center',
        gap: spacing.xs,
        paddingVertical: spacing.sm,
    },
    muted: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        textAlign: 'center',
    },
}));

export default FormProfileView;
